import React, { useState, useEffect } from 'react';
import AddReview from '../reviews/AddReview';
import Modal from '../common/Modal';
import { Link } from 'react-router-dom';

// Skeleton component matching SocialFeed skeleton style for loading
function SimpleRecSkeleton() {
  return (
    <div className="max-w-3xl mx-auto p-4">
      {[...Array(10)].map((_, i) => (
        <div key={i} className="border border-gray-300 rounded-lg p-4 mb-4 bg-white shadow-sm animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-1"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-3"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      ))}
    </div>
  );
}

const API_BASE = process.env.REACT_APP_API_BASE;
const API_KEY = process.env.REACT_APP_API_KEY;

function SimpleRecItem({ rec, onSeenIt }) {
  return (
    <div className="border border-gray-300 rounded-lg p-4 mb-4 bg-white shadow-sm">
      <div className="flex justify-between items-center mb-1">
        <h3 className="text-lg font-semibold text-indigo-600">{rec.film_name}</h3>
        <button
          type="button"
          className="bg-gray-200 text-gray-700 text-sm font-semibold px-3 py-1 rounded hover:bg-gray-300 transition-colors"
          onClick={() => onSeenIt(rec.film_name)}
        >
          Seen it!
        </button>
      </div>
      {(rec.content_type || rec.duration) && (
        <div className="text-sm font-medium text-gray-500 mb-1">
          {rec.content_type}
          {rec.content_type && rec.duration && ' - '}
          {rec.duration}
        </div>
      )}
      {rec.genre && (
        <div className="text-sm italic text-gray-600 mb-2">{rec.genre}</div>
      )}
      <p className="text-gray-700 whitespace-pre-line mb-2">
        {rec.film_description ? (
          rec.film_description
        ) : (
          <span className="text-gray-400 italic">No description provided</span>
        )}
      </p>
      <div className="flex gap-3 flex-wrap">
        {rec.deep_link_netflix && (
          <a
            href={rec.deep_link_netflix}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 hover:text-gray-300 transition-colors"
          >
            Netflix
          </a>
        )}
        {rec.deep_link_amazon && (
          <a
            href={rec.deep_link_amazon}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 hover:text-gray-300 transition-colors"
          >
            Prime Video
          </a>
        )}
        {rec.deep_link_disney && (
          <a
            href={rec.deep_link_disney}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 hover:text-gray-300 transition-colors"
          >
            Disney+
          </a>
        )}
        {rec.deep_link_canaldplus && (
          <a
            href={rec.deep_link_canaldplus}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-4 py-2 bg-black text-white rounded hover:bg-gray-800 hover:text-gray-300 transition-colors"
          >
            Canal+
          </a>
        )}
        {rec.deep_link_nowtv && (
          <a
            href={rec.deep_link_nowtv}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 hover:text-gray-300 transition-colors"
          >
            NOW TV
          </a>
        )}
        {rec.imdb_link && (
          <a
            href={rec.imdb_link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 hover:text-gray-300 transition-colors"
          >
            IMDb
          </a>
        )}
      </div>
    </div>
  );
}

// Animated dots component
function AnimatedDots() {
  const [dots, setDots] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return <span className="inline-block w-6 text-left">{dots}</span>;
}

export default function SimpleRec({ user }) {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Generating');
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [modalPrefillTitle, setModalPrefillTitle] = useState('');

  useEffect(() => {
    async function fetchRecs() {
      if (!user) {
        setLoading(false);
        return;
      }
      setError(null);
      try {
        const response = await fetch(`${API_BASE}/v1/recommendations/me`, {
          headers: {
            'X-API-Key': API_KEY,
            'Content-Type': 'application/json',
            'X-User-ID': user.id
          },
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to fetch recommendations');
        }
        let data = await response.json();
        data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setRecs(data.slice(0, 10));
      } catch (err) {
        setError(err.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    }

    fetchRecs();
  }, [user]);

  useEffect(() => {
    if (!generating || !loadingStartTime) return;

    const checkTime = () => {
      const elapsed = Date.now() - loadingStartTime;

      if (elapsed >= 10000) {
        setLoadingMessage('Nearly done');
      } else if (elapsed >= 5000) {
        setLoadingMessage('Patience');
      }
    };

    const interval = setInterval(checkTime, 100);

    return () => clearInterval(interval);
  }, [generating, loadingStartTime]);

  const handleSeenIt = (filmName) => {
    setModalPrefillTitle(filmName);
    setShowReviewModal(true);
  };

  const handleReviewSuccess = () => {
    setShowReviewModal(false);
    setModalPrefillTitle('');
  };

  const handleCloseModal = () => {
    setShowReviewModal(false);
    setModalPrefillTitle('');
  };

  if (loading) {
    return <SimpleRecSkeleton />;
  }

  if (error) return <div className="text-center text-red-600 py-8">Error: {error}</div>;

  if (!recs.length) return (
    <div className="text-center py-8">
      <span className="text-sm text-white font-space-grotesk text-center">
        You have no recommendations yet - <Link to="/reviews" className="text-indigo-400 hover:text-indigo-600 underline">add your first review</Link> to generate some recommendations
      </span>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-cling-pink">Recommendations</h2>
        <button
          onClick={async () => {
            try {
              setGenerating(true);
              setLoadingStartTime(Date.now());
              setLoadingMessage('Generating');
              setError(null);
              const response = await fetch(`${API_BASE}/v1/ai_recommendations/generate`, {
                method: 'POST',
                headers: {
                  'X-API-Key': API_KEY,
                  'Content-Type': 'application/json',
                  'X-User-ID': user.id
                },
              });
              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to generate recommendations');
              }
              const newRecs = await response.json();
              setRecs(prevRecs => {
                const combinedRecs = [...newRecs, ...prevRecs];
                combinedRecs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                return combinedRecs.slice(0, 10);
              });
            } catch (err) {
              setError(err.message || 'Failed to generate new recommendations');
            } finally {
              setGenerating(false);
              setLoadingStartTime(null);
            }
          }}
          disabled={generating}
          className={`px-4 py-2 bg-cling-pink text-white rounded transition-all min-w-[160px] ${
            generating ? 'opacity-90 cursor-not-allowed' : 'hover:bg-pink-700 hover:text-gray-300'
          }`}
        >
          {generating ? (
            <span className="flex items-center justify-center">
              {loadingMessage}<AnimatedDots />
            </span>
          ) : (
            'Give me more recs!'
          )}
        </button>
      </div>
      {recs.map((rec) => (
        <SimpleRecItem key={rec.id} rec={rec} onSeenIt={handleSeenIt} />
      ))}
      {showReviewModal && (
        <Modal isOpen={showReviewModal} onClose={handleCloseModal}>
          <AddReview
            prefilledName={modalPrefillTitle}
            variant="modal"
            onSuccess={handleReviewSuccess}
            onCancel={handleCloseModal}
          />
        </Modal>
      )}
    </div>
  );
}

